import React, { useEffect, useRef, ReactNode } from 'react';
import { useHistory } from 'react-router-dom';
import { MemoryHistory } from 'history';
import { useIntl } from 'react-intl';

import { useNetworkAndApiErrors } from 'hooks/error';

import Error from 'support/Error';
import textToSpeechManager from 'support/tts/TextToSpeechManager';

import LoadingOverlay from 'components/common/LoadingOverlay';

import ErrorModal from 'components/common/ErrorModal';

const messages = {
  loadingTts: {
    id: 'withLoading.tts',
    description: 'TTS for spinner',
  },
};

interface ErrorShape {
  title?: string;
  message: string;
  statusCode?: number;
}

interface WithLoadingProps {
  loading?: boolean;
  error?: ErrorShape | null;
  renderLoading?: (() => ReactNode) | null;
  renderError?: (() => ReactNode) | null;
  children?: ReactNode | null;
  positiveCTA?: string | null;
  negativeCTA?: string | null;
  onRetry?: (() => void) | null;
  onCancel?: (() => void) | null;
  ttsText?: string | null;
  enableTts?: boolean;
}

// Takes care of showing a loading spinner or error modal.
// The loading status (boolean) and error must be provided.
const WithLoading = ({
  loading = false,
  error = null,
  renderLoading = null,
  renderError = null,
  children = null,
  positiveCTA = null,
  negativeCTA = null,
  onRetry = null,
  onCancel = null,
  ttsText = null,
  enableTts = true,
}: WithLoadingProps) => {
  const intl = useIntl();
  const errorObject = error ? new Error(error) : null;
  const { title, message } = useNetworkAndApiErrors(errorObject);

  useEffect(() => {
    if (loading && enableTts) {
      textToSpeechManager.add(
        ttsText || intl.formatMessage(messages.loadingTts),
      );
      textToSpeechManager.speak();
    }
  }, [enableTts, intl, loading, ttsText]);

  const hasRetried = useRef(false);
  useEffect(() => {
    if (
      errorObject &&
      errorObject.isConnectionError() &&
      !hasRetried.current &&
      onRetry
    ) {
      hasRetried.current = true;
      onRetry();
    }
  }, [errorObject, onRetry]);

  const history = useHistory() as MemoryHistory;

  return (
    <>
      {children}
      {loading && (renderLoading ? renderLoading() : <LoadingOverlay />)}
      {!renderError && (
        <ErrorModal
          isOpen={!!error}
          title={title || 'Error'}
          message={message || 'Something went wrong.  Please try again.'}
          onCancel={
            onCancel ||
            (errorObject &&
              !errorObject.isConnectionError() &&
              (() => history.goBack())) ||
            null
          }
          onRetry={onRetry}
          positiveCTA={positiveCTA}
          negativeCTA={negativeCTA}
        />
      )}
      {error && renderError && renderError()}
    </>
  );
};

export default WithLoading;
